import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { ChevronDownIcon, ChevronRightIcon, ChevronUpIcon, PlusIcon, MinusIcon, CheckIcon, XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { normalizeGTINForStorage } from '../../libs/gtinUtils';
import GTINInput from '../../components/GTINInput.jsx';
import { primaryButtonClasses } from '../../libs/sharedClassNames.js';
import { insertShipper, updateShipper, deleteCasepackV1 } from '../../libs/fetch.js';
import { useNotification } from '../../contexts/CustomNotificationContext.jsx';

// Reusable Save/Cancel Buttons Component
const SaveCancelButtons = ({ 
  onSave, 
  onCancel, 
  isSaving, 
  hasChanges, 
  hasGtinErrors, 
  savingShipperId, 
  shipperId,
  size = "sm", // "sm" for row-level, "md" for contained products
  className = "",
  productsValidationError = null
}) => {
  const isCurrentlySaving = isSaving && savingShipperId === shipperId;
  const isDisabled = isCurrentlySaving || !hasChanges || hasGtinErrors || productsValidationError;
  
  const sizeClasses = {
    sm: "px-2 py-1 text-xs",
    md: "px-3 py-2 text-sm"
  };
  
  return (
    <>
      <button 
        onClick={onSave}
        disabled={isDisabled}
        className={`inline-flex items-center font-medium rounded-md mr-2 ${
          sizeClasses[size]
        } ${
          isDisabled
            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
            : 'text-primaryBrandColor border border-primaryBrandColor bg-secondaryBrandColor hover:bg-brandPeeledBananaYellow'
        } ${className}`}
      >
        {isCurrentlySaving ? 'Saving...' : 'Save'}
      </button>
      <button 
        onClick={onCancel}
        disabled={isCurrentlySaving}
        className={`inline-flex items-center font-medium rounded-md ${
          sizeClasses[size]
        } ${
          isCurrentlySaving
            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
            : 'text-white bg-red-600 hover:bg-red-700 border border-transparent'
        }`}
      >
        Cancel
      </button>
    </>
  );
};

// New Shipper Form Component
const NewShipperForm = ({ 
  isOpen, 
  onClose, 
  onSave, 
  isSaving,
  data,
  onNameChange,
  onGtinChange,
  onGTINValidation,
  products,
  onAddProduct,
  onUpdateProductQuantity,
  onRemoveProduct,
  hasChanges,
  isValid,
  searchRef,
  getProductsError,
  productSearchTerm,
  showProductDropdown,
  isSearching,
  onProductSearchChange,
  onToggleProductDropdown,
  onClearProductSearch,
  onAddProductFromSearch,
  filteredProducts,
  getQuantityWarning
}) => {
  
  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium text-gray-900">Create New Shipper</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Form Fields */}
          <div className="space-y-6">
            {/* Shipper Name */}
            <div>
              <label htmlFor="shipper-name" className="block text-sm font-medium text-gray-700 mb-2">
                Shipper Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="shipper-name"
                value={data.name}
                onChange={(e) => onNameChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primaryBrandColor focus:border-primaryBrandColor"
                placeholder="Enter shipper name"
              />
            </div>

            {/* GTIN */}
            <div>
              <label htmlFor="shipper-gtin" className="block text-sm font-medium text-gray-700 mb-2">
                GTIN (Optional)
              </label>
              <GTINInput
                value={data.gtin}
                onChange={(value) => onGtinChange(value)}
                onValidationChange={onGTINValidation}
                placeholder="Enter GTIN"
                className="w-full"
                maxLength={14}
              />
            </div>

            {/* Consumer Products Section */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700">
                  Consumer Products
                </label>
                <span className="text-sm text-gray-500">
                  {products.length} product{products.length !== 1 ? 's' : ''} added
                </span>
              </div>

              {/* Products Table */}
              {products.length > 0 ? (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Product
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Quantity
                        </th>
                        <th className="relative px-3 py-2">
                          <span className="sr-only">Actions</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {products.map((product) => (
                        <tr key={product.id} className="hover:bg-gray-50">
                          <td className="px-3 py-3 text-sm">
                            <div>
                              <div className="font-medium text-gray-900">{product.product_name}</div>
                              {product.ean_upc && (
                                <div className="text-xs text-gray-500">{product.ean_upc}</div>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-3 text-sm text-gray-900">
                            <div className="flex items-center">
                              <input
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                value={product.quantity === '' || product.quantity === null || product.quantity === undefined ? '' : String(product.quantity)}
                                onChange={(e) => {
                                  const inputValue = e.target.value;
                                  
                                  // Allow empty string for better UX
                                  if (inputValue === '') {
                                    onUpdateProductQuantity(product.id, '');
                                    return;
                                  }
                                  
                                  // Only allow digits
                                  if (!/^\d+$/.test(inputValue)) {
                                    return;
                                  }
                                  
                                  // Keep as string while typing, but clean leading zeros
                                  let cleanedValue = inputValue;
                                  if (inputValue.startsWith('0') && inputValue.length > 1) {
                                    cleanedValue = inputValue.replace(/^0+/, '') || '0';
                                  }
                                  
                                  onUpdateProductQuantity(product.id, cleanedValue);
                                }}
                                className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primaryBrandColor focus:border-primaryBrandColor"
                              />
                              {getQuantityWarning && getQuantityWarning(product.id) && (
                                <div className="ml-2 relative group">
                                  <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600" />
                                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none whitespace-nowrap z-50">
                                    {getQuantityWarning(product.id)}
                                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-3 text-sm text-right">
                            <button
                              onClick={() => onRemoveProduct(product.id)}
                              className="text-red-600 hover:text-red-800"
                            >
                              <MinusIcon className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>No products added yet. Use the search below to add products.</p>
                </div>
              )}

              {/* Product Search */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Add Products
                </label>
                <div className="relative" ref={searchRef}>
                  <input
                    type="text"
                    placeholder="Search for products to add..."
                    value={productSearchTerm}
                    onChange={(e) => onProductSearchChange(e.target.value)}
                    onClick={() => {
                      if (!isSearching) {
                        onToggleProductDropdown();
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primaryBrandColor focus:border-primaryBrandColor"
                  />
                  
                  {/* Clear button */}
                  {productSearchTerm && (
                    <button
                      type="button"
                      onClick={onClearProductSearch}
                      className="absolute right-8 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 hover:text-gray-600 transition-colors"
                      title="Clear search"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  )}
                  
                  {/* Dropdown toggle button */}
                  <button
                    type="button"
                    onClick={onToggleProductDropdown}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 hover:text-gray-600 transition-transform"
                  >
                    <ChevronDownIcon 
                      className={`h-4 w-4 transition-transform ${
                        showProductDropdown ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  
                  {/* Product Dropdown */}
                  {showProductDropdown && (
                    <div 
                      data-dropdown
                      className="absolute z-50 mt-1 w-full max-h-60 overflow-y-auto border-2 border-gray-300 rounded-lg bg-white shadow-xl"
                    >
                      {filteredProducts.length > 0 ? (
                        filteredProducts.map((product) => (
                          <button
                            key={product.id}
                            onClick={() => onAddProductFromSearch(product)}
                            className="w-full text-left p-2 hover:bg-gray-100 border-b border-gray-200 last:border-b-0"
                          >
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="text-sm font-medium text-gray-900">{product.product_name}</div>
                                {product.ean_upc && (
                                  <div className="text-xs text-gray-500">{product.ean_upc}</div>
                                )}
                              </div>
                              {product.product_line && (
                                <div className="text-xs text-gray-400 ml-2 text-right">
                                  {product.product_line}
                                </div>
                              )}
                            </div>
                          </button>
                        ))
                      ) : (
                        <div className="p-3 text-gray-500 text-sm">
                          {productSearchTerm ? 'No products found matching your search.' : 'No available products to add.'}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                <p className="mt-1 text-xs text-gray-500">
                  Search and select products to include in this shipper
                </p>
              </div>
            </div>
          </div>

          {/* Products Validation Error */}
          {getProductsError() && (
            <div className="mt-4 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-md border border-red-200">
              {getProductsError()}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 mt-8">
            <button
              onClick={onClose}
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primaryBrandColor"
            >
              Cancel
            </button>
            <button
              onClick={onSave}
              disabled={isSaving || !isValid}
              className={`px-4 py-2 text-sm font-medium rounded-md ${
                isSaving || !isValid
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'text-white bg-primaryBrandColor hover:bg-primaryBrandColor/80'
              }`}
            >
              {isSaving ? 'Creating...' : 'Create Shipper'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Shippers = ({ shippers, consumerProducts, onRefresh, organizationId, canEditProducts = true }) => {
  const { sendNotification } = useNotification();
  const [expandedShippers, setExpandedShippers] = useState(new Set());
  const [editingShipperId, setEditingShipperId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [savingShipperId, setSavingShipperId] = useState(null);
  const [shipperEditData, setShipperEditData] = useState({});
  const [originalShipperData, setOriginalShipperData] = useState({});
  const [containedProductsEditData, setContainedProductsEditData] = useState({});
  const [originalContainedProductsData, setOriginalContainedProductsData] = useState({});
  const [gtinValidationErrors, setGtinValidationErrors] = useState({});
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [showNewShipperModal, setShowNewShipperModal] = useState(false);
  const [newShipperData, setNewShipperData] = useState({
    name: '',
    gtin: ''
  });
  const [newShipperProducts, setNewShipperProducts] = useState([]);
  const [newShipperGtinError, setNewShipperGtinError] = useState(null);
  const [newShipperProductSearchTerm, setNewShipperProductSearchTerm] = useState('');
  const [newShipperShowProductDropdown, setNewShipperShowProductDropdown] = useState(false);
  const [newShipperIsSearching, setNewShipperIsSearching] = useState(false);
  const searchRef = useRef(null);
  const newShipperSearchRef = useRef(null);


  const toggleExpanded = (shipperId) => {
    setExpandedShippers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(shipperId)) {
        newSet.delete(shipperId);
      } else {
        newSet.add(shipperId);
      }
      return newSet;
    });
  };

  const toggleAllExpanded = () => {
    // Check if all shippers are expanded
    const allExpanded = shippers.length > 0 && shippers.every(s => expandedShippers.has(s.id));
    
    if (allExpanded) {
      // Collapse all
      setExpandedShippers(new Set());
    } else {
      // Expand all
      const allShipperIds = new Set(shippers.map(s => s.id));
      setExpandedShippers(allShipperIds);
    }
  };

  const startInlineEditing = (shipperId) => {
    // Stop any other editing first
    if (editingShipperId && editingShipperId !== shipperId) {
      stopInlineEditing(editingShipperId);
    }
    
    // Ensure the shipper is expanded
    if (!expandedShippers.has(shipperId)) {
      setExpandedShippers(prev => {
        const newSet = new Set(prev);
        newSet.add(shipperId);
        return newSet;
      });
    }
    
    setEditingShipperId(shipperId);
    
    // Initialize editing data with current values
    const shipper = shippers.find(s => s.id === shipperId);
    if (shipper) {
      setShipperEditData(prev => ({
        ...prev,
        [shipperId]: {
          name: shipper.name || '',
          gtin: shipper.gtin || ''
        }
      }));
      setOriginalShipperData(prev => ({
        ...prev,
        [shipperId]: {
          name: shipper.name || '',
          gtin: shipper.gtin || ''
        }
      }));
      
      // Start contained products editing
      startContainedProductsEditing(shipperId);
    }
  };

  const stopInlineEditing = (shipperId) => {
    setEditingShipperId(null);
    // Revert any unsaved changes
    setShipperEditData(prev => {
      const newData = { ...prev };
      delete newData[shipperId];
      return newData;
    });
    setOriginalShipperData(prev => {
      const newData = { ...prev };
      delete newData[shipperId];
      return newData;
    });
    setGtinValidationErrors(prev => {
      const newData = { ...prev };
      delete newData[shipperId];
      return newData;
    });
    
    // Stop contained products editing
    stopContainedProductsEditing(shipperId);
  };

  // Validation functions for inline editing
  const isShipperNameValid = (shipperId) => {
    const shipperData = shipperEditData[shipperId];
    return shipperData?.name && shipperData.name.trim() !== '';
  };

  const hasShipperGTINConflict = (shipperId) => {
    const shipperData = shipperEditData[shipperId];
    const gtinErrors = gtinValidationErrors[shipperId];
    return gtinErrors?.hasError || false;
  };

  const saveInlineEdit = async (shipperId) => {
    try {
      // Validate the data before saving
      if (!isShipperNameValid(shipperId) || hasShipperGTINConflict(shipperId)) {
        alert('Please fix all validation errors before saving.');
        return;
      }

      // Check if there are at least 2 products with quantities > 0
      const containedProducts = containedProductsEditData[shipperId] || [];
      const productsWithQuantity = containedProducts.filter(product => product.quantity > 0);
      
      if (productsWithQuantity.length < 2) {
        alert('Shipper must contain at least 2 products with quantities greater than 0.');
        return;
      }

      setIsSaving(true);
      setSavingShipperId(shipperId);

      // Prepare the data for the database
      const shipperData = {
        name: shipperEditData[shipperId]?.name || null,
        gtin: shipperEditData[shipperId]?.gtin ? normalizeGTINForStorage(shipperEditData[shipperId].gtin) : null,
        consumer_products: {}
      };

      // Convert contained products array to object with product IDs as keys and quantities as values
      containedProducts
        .filter(product => product.quantity > 0)
        .forEach(product => {
          shipperData.consumer_products[product.id] = product.quantity;
        });

      // Call the actual API function
      const result = await updateShipper(shipperId, shipperData);
      
      if (result.error) {
        console.error('Error updating shipper:', result.error);
        alert('Error updating shipper. Please try again.');
        return;
      }
      
      // Refresh data first, then clear editing state to avoid flash
      await onRefresh();
      
      setEditingShipperId(null);
      
      // Clear the editing data
      setShipperEditData(prev => {
        const newData = { ...prev };
        delete newData[shipperId];
        return newData;
      });
      setOriginalShipperData(prev => {
        const newData = { ...prev };
        delete newData[shipperId];
        return newData;
      });
      setContainedProductsEditData(prev => {
        const newData = { ...prev };
        delete newData[shipperId];
        return newData;
      });
      setOriginalContainedProductsData(prev => {
        const newData = { ...prev };
        delete newData[shipperId];
        return newData;
      });
    } catch (error) {
      console.error('Error saving inline edit:', error);
      alert('Error saving changes. Please try again.');
    } finally {
      setIsSaving(false);
      setSavingShipperId(null);
    }
  };

  const handleShipperEditChange = (shipperId, field, value) => {
    setShipperEditData(prev => ({
      ...prev,
      [shipperId]: {
        ...prev[shipperId],
        [field]: value
      }
    }));
  };

  const handleGTINValidationChange = useCallback((shipperId, validation) => {
    setGtinValidationErrors(prev => ({
      ...prev,
      [shipperId]: validation
    }));
  }, []);

  // New Shipper Functions
  const openNewShipperModal = () => {
    setShowNewShipperModal(true);
    setNewShipperData({ name: '', gtin: '' });
    setNewShipperProducts([]);
    setNewShipperGtinError(null);
  };

  const closeNewShipperModal = () => {
    setShowNewShipperModal(false);
    setNewShipperData({ name: '', gtin: '' });
    setNewShipperProducts([]);
    setNewShipperGtinError(null);
  };

  const handleNewShipperGTINValidation = (validation) => {
    setNewShipperGtinError(validation.hasError ? validation.errorMessage : null);
  };

  const addProductToNewShipper = (product) => {
    // Check if product is already added
    if (newShipperProducts.find(p => p.id === product.id)) {
      return;
    }
    
    setNewShipperProducts(prev => [
      ...prev,
      {
        ...product,
        quantity: '',
        isNewlyAdded: true
      }
    ]);
  };

  const updateNewShipperProductQuantity = (productId, newQuantity) => {
    setNewShipperProducts(prev => 
      prev.map(product => 
        product.id === productId 
          ? { ...product, quantity: newQuantity }
          : product
      )
    );
  };

  const removeProductFromNewShipper = (productId) => {
    setNewShipperProducts(prev => 
      prev.filter(product => product.id !== productId)
    );
  };

  const saveNewShipper = async () => {
    if (!newShipperData.name.trim()) {
      return; // Name is required
    }

    if (newShipperGtinError) {
      return; // GTIN has validation errors
    }

    if (!validateNewShipperProducts()) {
      console.error('Cannot save: new shipper must have at least 2 products with quantities');
      return;
    }

    try {
      setIsSaving(true);

      // Prepare the new shipper data
      const shipperData = {
        organization_id: organizationId,
        name: newShipperData.name.trim() || null,
        gtin: newShipperData.gtin.trim() ? normalizeGTINForStorage(newShipperData.gtin.trim()) : null,
        consumer_products: {}
      };

      // Convert products array to object with product IDs as keys and quantities as values
      newShipperProducts
        .filter(product => {
          const qty = typeof product.quantity === 'string' ? parseInt(product.quantity) || 0 : product.quantity;
          return qty > 0;
        })
        .forEach(product => {
          const qty = typeof product.quantity === 'string' ? parseInt(product.quantity) : product.quantity;
          shipperData.consumer_products[product.id] = qty;
        });

      // Call the actual API function
      const result = await insertShipper(shipperData);
      
      if (result.error) {
        console.error('Error creating shipper:', result.error);
        alert('Error creating shipper. Please try again.');
        return;
      }
      
      // Refresh data first, then clear state to avoid flash
      await onRefresh();
      
      // Success! Close modal
      setShowNewShipperModal(false);
      
      // Reset form data
      setNewShipperData({ name: '', gtin: '' });
      setNewShipperProducts([]);
      setNewShipperProductSearchTerm('');
      setNewShipperShowProductDropdown(false);
      setNewShipperGtinError(null);
      setNewShipperIsSearching(false);
    } catch (error) {
      console.error('Error creating new shipper:', error);
      alert('Error creating shipper. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const hasNewShipperChanges = () => {
    return newShipperData.name.trim() !== '' || 
           newShipperData.gtin.trim() !== '' || 
           newShipperProducts.length > 0;
  };

  const isNewShipperFormValid = () => {
    return newShipperData.name.trim() !== '' && 
           !newShipperGtinError &&
           validateNewShipperProducts();
  };

  // Validate that new shipper has at least 2 products with quantities
  const validateNewShipperProducts = () => {
    const productsWithQuantity = newShipperProducts.filter(product => {
      const qty = typeof product.quantity === 'string' ? parseInt(product.quantity) || 0 : product.quantity;
      return qty > 0;
    });
    return productsWithQuantity.length >= 2;
  };

  // Get validation error message for new shipper products
  const getNewShipperProductsError = () => {
    if (validateNewShipperProducts()) return null;
    
    const productsWithQuantity = newShipperProducts.filter(product => {
      const qty = typeof product.quantity === 'string' ? parseInt(product.quantity) || 0 : product.quantity;
      return qty > 0;
    });
    
    if (productsWithQuantity.length === 0) {
      return "At least 2 products with quantities are required.";
    } else if (productsWithQuantity.length === 1) {
      return "At least 1 more product with quantity is required.";
    }
    return null;
  };

  // New Shipper Product Search Functions
  const getAvailableProductsForNewShipper = () => {
    if (!consumerProducts) return [];
    
    // Get IDs of products already added to the new shipper
    const existingProductIds = newShipperProducts.map(p => p.id);
    
    // Filter out products that are already added
    return consumerProducts.filter(product => !existingProductIds.includes(product.id));
  };

  const getFilteredProductsForNewShipper = () => {
    const availableProducts = getAvailableProductsForNewShipper();
    if (!newShipperProductSearchTerm) return availableProducts;
    
    const searchTerm = newShipperProductSearchTerm.toLowerCase();
    
    return availableProducts.filter(product => {
      // Search across multiple fields - same as existing search
      const searchableFields = [
        product.product_name,
        product.ean_upc,
        product.product_line,
      ];
      
      return searchableFields.some(field => 
        field && field.toLowerCase().includes(searchTerm)
      );
    });
  };

  const handleNewShipperProductSearch = (searchTerm) => {
    setNewShipperProductSearchTerm(searchTerm);
    if (searchTerm.trim()) {
      setNewShipperShowProductDropdown(true);
      setNewShipperIsSearching(true);
    } else {
      setNewShipperIsSearching(false);
    }
  };

  const toggleNewShipperProductDropdown = () => {
    setNewShipperShowProductDropdown(!newShipperShowProductDropdown);
  };

  const clearNewShipperProductSearch = () => {
    setNewShipperProductSearchTerm('');
    setNewShipperIsSearching(false);
  };

  const addProductToNewShipperFromSearch = (product) => {
    addProductToNewShipper(product);
    setNewShipperProductSearchTerm('');
    setNewShipperShowProductDropdown(false);
    setNewShipperIsSearching(false);
  };

  // Close new shipper product dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (newShipperSearchRef.current && !newShipperSearchRef.current.contains(event.target)) {
        // Only close if not actively searching
        if (!newShipperIsSearching) {
          setNewShipperShowProductDropdown(false);
        }
      }
    };

    if (newShipperShowProductDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [newShipperShowProductDropdown, newShipperIsSearching]);

  const hasFieldChanges = (shipperId, field) => {
    const original = originalShipperData[shipperId];
    const current = shipperEditData[shipperId];
    
    if (!original || !current) return false;
    
    // Handle empty values properly - treat empty strings as null for comparison
    const normalizeValue = (val) => {
      if (val === null || val === undefined || val === '') return null;
      return val.toString().trim();
    };
    
    const normalizedOriginal = normalizeValue(original[field]);
    const normalizedCurrent = normalizeValue(current[field]);
    
    return normalizedOriginal !== normalizedCurrent;
  };

  // Check if any fields have changed for a specific shipper
  const hasAnyChanges = useCallback((shipperId) => {
    const original = originalShipperData[shipperId];
    const current = shipperEditData[shipperId];
    
    if (!original || !current) return false;
    
    // Check name changes
    if (original.name !== current.name) return true;
    
    // Check GTIN changes (normalize for comparison)
    const originalGtin = original.gtin ? normalizeGTINForStorage(original.gtin) : null;
    const currentGtin = current.gtin ? normalizeGTINForStorage(current.gtin) : null;
    if (originalGtin !== currentGtin) return true;
    
    // Check contained products changes
    const originalProducts = originalContainedProductsData[shipperId] || [];
    const currentProducts = containedProductsEditData[shipperId] || [];
    
    if (originalProducts.length !== currentProducts.length) return true;
    
    // Check if any product quantities have changed
    for (let i = 0; i < originalProducts.length; i++) {
      const originalProduct = originalProducts[i];
      const currentProduct = currentProducts.find(p => p.id === originalProduct.id);
      
      if (!currentProduct || originalProduct.quantity !== currentProduct.quantity) {
        return true;
      }
    }
    
    return false;
  }, [originalShipperData, shipperEditData, containedProductsEditData, gtinValidationErrors]);

  // Validate that a shipper has at least 2 products with quantities
  const validateShipperProducts = (shipperId) => {
    const currentProducts = containedProductsEditData[shipperId] || [];
    const productsWithQuantity = currentProducts.filter(product => product.quantity > 0);
    return productsWithQuantity.length >= 2;
  };

  // Get validation error message for shipper products
  const getShipperProductsError = (shipperId) => {
    if (validateShipperProducts(shipperId)) return null;
    
    const currentProducts = containedProductsEditData[shipperId] || [];
    const productsWithQuantity = currentProducts.filter(product => product.quantity > 0);
    
    if (productsWithQuantity.length === 0) {
      return "At least 2 products with quantities are required.";
    } else if (productsWithQuantity.length === 1) {
      return "At least 1 more product with quantity is required.";
    }
    return null;
  };

  const startContainedProductsEditing = (shipperId) => {
    const shipper = shippers.find(s => s.id === shipperId);
    const currentProducts = shipper?.consumer_products || [];
    
    setContainedProductsEditData(prev => ({
      ...prev,
      [shipperId]: currentProducts.map(product => ({ ...product }))
    }));
    setOriginalContainedProductsData(prev => ({
      ...prev,
      [shipperId]: currentProducts.map(product => ({ ...product }))
    }));
  };

  const stopContainedProductsEditing = (shipperId) => {
    setContainedProductsEditData(prev => {
      const newData = { ...prev };
      delete newData[shipperId];
      return newData;
    });
    setOriginalContainedProductsData(prev => {
      const newData = { ...prev };
      delete newData[shipperId];
      return newData;
    });
    setShowProductDropdown(false);
    setProductSearchTerm('');
    setIsSearching(false);
  };

  const updateContainedProductQuantity = (shipperId, productId, newQuantity) => {
    setContainedProductsEditData(prev => ({
      ...prev,
      [shipperId]: prev[shipperId].map(product => 
        product.id === productId 
          ? { 
              ...product, 
              quantity: newQuantity,
              // If quantity is >0, remove the "removed" flag
              // Allow empty string to remain as empty string
              removed: (newQuantity === '' || newQuantity === 0) ? product.removed : false
            }
          : product
      )
    }));
  };

  const getShipperQuantityWarning = (shipperId, productId) => {
    if (editingShipperId !== shipperId) return null;
    const product = containedProductsEditData[shipperId]?.find(p => p.id === productId);
    if (!product) return null;
    const qty = typeof product.quantity === 'string' ? parseInt(product.quantity) || 0 : (product.quantity || 0);
    if (qty > 999) {
      return 'Large quantity detected. Please double-check this value.';
    }
    return null;
  };

  const getNewShipperQuantityWarning = (productId) => {
    const product = newShipperProducts.find(p => p.id === productId);
    if (!product) return null;
    const qty = typeof product.quantity === 'string' ? parseInt(product.quantity) || 0 : (product.quantity || 0);
    if (qty > 999) {
      return 'Large quantity detected. Please double-check this value.';
    }
    return null;
  };

  const removeContainedProduct = (shipperId, productId) => {
    setContainedProductsEditData(prev => {
      const currentProducts = prev[shipperId] || [];
      const productToRemove = currentProducts.find(p => p.id === productId);
      
      if (productToRemove?.isNewlyAdded) {
        // For newly added products, remove them entirely
        return {
          ...prev,
          [shipperId]: currentProducts.filter(product => product.id !== productId)
        };
      } else {
        // For existing products, mark as removed
        return {
          ...prev,
          [shipperId]: currentProducts.map(product => 
            product.id === productId 
              ? { ...product, quantity: 0, removed: true }
              : product
          )
        };
      }
    });
  };

  const addNewProduct = (shipperId, productId, productName, productGtin) => {
    const newProduct = {
      id: productId,
      product_name: productName,
      gtin: productGtin,
      quantity: '',
      isNewlyAdded: true
    };
    
    setContainedProductsEditData(prev => ({
      ...prev,
      [shipperId]: [...(prev[shipperId] || []), newProduct]
    }));
  };

  const hasContainedProductsChanges = (shipperId) => {
    const original = originalContainedProductsData[shipperId] || [];
    const current = containedProductsEditData[shipperId] || [];
    
    // Check if any new products were added
    const newProducts = current.filter(product => product.isNewlyAdded);
    if (newProducts.length > 0) return true;
    
    // Check if any existing products were modified or removed
    for (let i = 0; i < original.length; i++) {
      const currentProduct = current.find(p => p.id === original[i].id);
      if (!currentProduct || currentProduct.removed || currentProduct.quantity !== original[i].quantity) {
        return true;
      }
    }
    
    return false;
  };

  const getAvailableConsumerProducts = (shipperId) => {
    if (!consumerProducts) return [];
    
    const currentShipper = shippers.find(s => s.id === shipperId);
    if (!currentShipper) return consumerProducts;
    
    // Get current products from edit data or original data
    const currentProducts = containedProductsEditData[shipperId] || currentShipper.consumer_products || [];
    
    // Get IDs of products currently in this shipper (including newly added ones)
    const existingProductIds = currentProducts.map(p => p.id);
    
    // Filter out products that are already in the shipper
    return consumerProducts.filter(product => !existingProductIds.includes(product.id));
  };

  const getFilteredAvailableProducts = (shipperId) => {
    const availableProducts = getAvailableConsumerProducts(shipperId);
    if (!productSearchTerm) return availableProducts;
    
    const searchTerm = productSearchTerm.toLowerCase();
    
    return availableProducts.filter(product => {
      // Search across multiple fields - easily extensible for future additions
      const searchableFields = [
        product.product_name,
        product.ean_upc,
        product.product_line,
        // Future fields can be added here:
        // product.customer_sku,
        // product.reporting_name,
        // etc.
      ];
      
      return searchableFields.some(field => 
        field && field.toLowerCase().includes(searchTerm)
      );
    });
  };

  // Helper function to get original quantity for a product
  const getOriginalQuantity = (shipperId, productId) => {
    const original = originalContainedProductsData[shipperId] || [];
    const originalProduct = original.find(p => p.id === productId);
    return originalProduct ? originalProduct.quantity : 0;
  };

  // Helper function to check if a product has quantity changes
  const hasQuantityChanges = (shipperId, productId) => {
    const original = getOriginalQuantity(shipperId, productId);
    const current = containedProductsEditData[shipperId]?.find(p => p.id === productId)?.quantity || 0;
    return original !== current;
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        // Only close if not actively searching
        if (!isSearching) {
          setShowProductDropdown(false);
        }
      }
    };

    if (showProductDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProductDropdown, isSearching]);

  // Auto-scroll to dropdown when it opens
  useEffect(() => {
    if (showProductDropdown && searchRef.current) {
      // Wait a bit for the dropdown to render, then scroll it into view
      setTimeout(() => {
        const dropdownElement = searchRef.current?.querySelector('[data-dropdown]');
        if (dropdownElement) {
          dropdownElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'nearest',
            inline: 'nearest'
          });
        }
      }, 100);
    }
  }, [showProductDropdown]);



  if (shippers.length === 0) {
    return (
      <div>
        <div className="text-center py-12">
          <div className="mx-auto h-12 w-12 text-gray-400">
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
          </div>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No shippers</h3>
          <p className="mt-1 text-sm text-gray-500">Get started by creating your first shipper.</p>
          {canEditProducts && (
            <div className="mt-6">
              <button
                type="button"
                className="inline-flex items-center px-6 py-2 border-2 border-primaryBrandColor rounded-md shadow-sm text-sm font-medium text-primaryBrandColor bg-secondaryBrandColor hover:bg-brandPeeledBananaYellow focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primaryBrandColor whitespace-nowrap h-10"
                onClick={() => {
                  openNewShipperModal();
                }}
              >
                <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
                New Shipper
              </button>
            </div>
          )}
        </div>

        {/* New Shipper Modal - Always render this */}
        <NewShipperForm
          isOpen={showNewShipperModal}
          onClose={closeNewShipperModal}
          onSave={saveNewShipper}
          isSaving={isSaving}
          data={newShipperData}
          onNameChange={(value) => setNewShipperData(prev => ({ ...prev, name: value }))}
          onGtinChange={(value) => setNewShipperData(prev => ({ ...prev, gtin: value }))}
          onGTINValidation={handleNewShipperGTINValidation}
          products={newShipperProducts}
          onAddProduct={addProductToNewShipper}
          onUpdateProductQuantity={updateNewShipperProductQuantity}
          onRemoveProduct={removeProductFromNewShipper}
          hasChanges={hasNewShipperChanges()}
          isValid={isNewShipperFormValid()}
          searchRef={newShipperSearchRef}
          getProductsError={getNewShipperProductsError}
          productSearchTerm={newShipperProductSearchTerm}
          showProductDropdown={newShipperShowProductDropdown}
          isSearching={newShipperIsSearching}
          onProductSearchChange={handleNewShipperProductSearch}
          onToggleProductDropdown={toggleNewShipperProductDropdown}
          onClearProductSearch={clearNewShipperProductSearch}
          onAddProductFromSearch={addProductToNewShipperFromSearch}
          filteredProducts={getFilteredProductsForNewShipper()}
          getQuantityWarning={getNewShipperQuantityWarning}
        />
      </div>
    );
  }

  return (
    <div>
      {/* Table Controls */}
      <div className="flex justify-between items-center mb-4">
        {/* Tab Title */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Shippers</h2>
        </div>
        
        {/* Action Buttons */}
        <div>
          <div className="flex gap-2">
            {canEditProducts && (
              <button
                type="button"
                className={`inline-flex items-center px-4 py-2 text-sm font-medium ${primaryButtonClasses}`}
                onClick={openNewShipperModal}
              >
                <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
                New Shipper
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Shippers Table */}
      <div className="mt-2">
        <div className="py-2">
          <div className="w-full">
            <div className="shadow ring-1 ring-black ring-opacity-5 md:rounded-lg overflow-visible">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 w-16">
                      <button
                        onClick={toggleAllExpanded}
                        disabled={shippers.length === 0}
                        className="text-gray-400 hover:text-gray-600 disabled:text-gray-300 disabled:cursor-not-allowed"
                        title={shippers.length > 0 && shippers.every(s => expandedShippers.has(s.id)) ? "Collapse all" : "Expand all"}
                      >
                        {shippers.length > 0 && shippers.every(s => expandedShippers.has(s.id)) ? (
                          <ChevronDownIcon className="h-4 w-4" />
                        ) : (
                          <ChevronRightIcon className="h-4 w-4" />
                        )}
                      </button>
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Name
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-40">
                      GTIN
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-32">
                      Components
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-32">
                      Total Units
                    </th>
                    <th scope="col" className="relative py-3.5 pl-3 pr-4 w-32">
                      <span className="sr-only">Edit</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white overflow-visible">
                  {shippers.map((shipper) => (
                    <React.Fragment key={shipper.id}>
                      <tr className="hover:bg-gray-50">
                        <td className="whitespace-nowrap py-2 pl-4 pr-1 text-sm text-gray-500 w-16">
                          <div className="w-8 flex items-center">
                            <button
                              onClick={() => toggleExpanded(shipper.id)}
                              className="text-gray-400 hover:text-gray-600 flex items-center"
                            >
                              <span className="text-xs">
                                ({shipper.consumer_products?.length || 0})
                              </span>
                              {expandedShippers.has(shipper.id) ? (
                                <ChevronDownIcon className="h-4 w-4" />
                              ) : (
                                <ChevronRightIcon className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900">
                          {(editingShipperId === shipper.id) ? (
                            <div className="relative flex items-center h-8">
                              <input
                                type="text"
                                value={shipperEditData[shipper.id]?.name !== undefined ? shipperEditData[shipper.id].name : shipper.name}
                                onChange={(e) => handleShipperEditChange(shipper.id, 'name', e.target.value)}
                                className={`w-full px-0 py-0 text-sm border border-gray-200 focus:border-green-400 focus:ring-1 focus:ring-green-400 focus:outline-none text-gray-900 h-8 ${
                                  hasFieldChanges(shipper.id, 'name') ? 'bg-yellow-50 border-yellow-300' : 'bg-gray-50'
                                }`}
                              />
                            </div>
                          ) : (
                            <div className="flex items-center h-8">
                              <div className="flex items-center">
                                {shipper.name}
                                {!shipper.is_active && (
                                  <span className="ml-2 inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">
                                    Inactive
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 w-40">
                          {(editingShipperId === shipper.id) ? (
                            <div className="w-40">
                              <GTINInput
                                value={shipperEditData[shipper.id]?.gtin !== undefined ? shipperEditData[shipper.id].gtin : shipper.gtin || ''}
                                onChange={(value) => handleShipperEditChange(shipper.id, 'gtin', value)}
                                onValidationChange={(validation) => handleGTINValidationChange(shipper.id, validation)}
                                placeholder="Enter GTIN"
                                alwaysShowPadding={true}
                                excludeProductId={shipper.id}
                                excludeCasepackId={null}
                                maxLength={14}
                              />
                            </div>
                          ) : (
                            <div className="w-40 overflow-hidden">
                              <span>{shipper.gtin || '(none)'}</span>
                            </div>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 w-32">
                          {shipper.consumer_products?.length || 0} products
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 w-32">
                          {shipper.total_units || 0} units
                        </td>
                        <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium w-32 min-w-[128px] max-w-[128px] overflow-hidden">
                          <div className="flex items-center justify-end h-8 w-full">
                            {editingShipperId === shipper.id ? (
                              <div className="text-sm text-gray-500">
                                Editing...
                              </div>
                            ) : (
                              canEditProducts && (
                                <div className="flex items-center gap-3">
                                  <button 
                                    onClick={() => startInlineEditing(shipper.id)}
                                    disabled={isSaving || editingShipperId !== null}
                                    className={`text-sm ${
                                      isSaving || editingShipperId !== null
                                        ? 'text-gray-400 cursor-not-allowed'
                                        : 'text-primaryBrandColor hover:text-primaryBrandColor/80'
                                    }`}
                                  >
                                    Edit
                                  </button>
                                  <button 
                                    onClick={async () => {
                                      if (!confirm('Are you sure you want to delete this shipper?')) {
                                        return;
                                      }
                                      const result = await deleteCasepackV1(shipper.id);
                                      if (result !== false) {
                                        onRefresh();
                                      } else {
                                        sendNotification('Error', 'Failed to delete shipper. Please try again.', true);
                                      }
                                    }}
                                    disabled={isSaving || editingShipperId !== null}
                                    className={`text-sm ${
                                      isSaving || editingShipperId !== null
                                        ? 'text-gray-400 cursor-not-allowed'
                                        : 'text-red-600 hover:text-red-900'
                                    }`}
                                  >
                                    Delete
                                  </button>
                                </div>
                              )
                            )}
                          </div>
                        </td>
                      </tr>
                                             {/* Expanded Products Row */}
                       {expandedShippers.has(shipper.id) && (
                         <tr>
                           <td colSpan="6" className="bg-gray-50 px-6 py-4 overflow-visible">
                            <div className="text-sm text-gray-900 font-medium mb-3">Contained Products</div>
                            {shipper.consumer_products && shipper.consumer_products.length > 0 ? (
                              <div className="shadow ring-1 ring-black ring-opacity-5 rounded-lg overflow-visible">
                                <table className="min-w-full divide-y divide-gray-300 overflow-visible">
                                  <thead className="bg-gray-100">
                                    <tr>
                                      <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Product
                                      </th>
                                      <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[96px] max-w-[96px] pl-20">
                                        Quantity
                                      </th>
                                      <th scope="col" className="relative px-3 py-2 w-24">
                                        <span className="sr-only">Actions</span>
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className="bg-white divide-y divide-gray-200 overflow-visible">
                                                            {(editingShipperId === shipper.id ? containedProductsEditData[shipper.id] : shipper.consumer_products)?.map((product) => (
                          <tr key={product.id} className={`${
                            product.removed ? 'bg-red-50 hover:bg-red-100' : 
                            product.isNewlyAdded ? 'bg-green-50 hover:bg-green-100' : 
                            hasQuantityChanges(shipper.id, product.id) ? 'bg-yellow-50 hover:bg-yellow-100' :
                            'hover:bg-gray-50'
                          }`}>
                                        <td className="px-3 py-3 text-sm">
                                          <div>
                                            <div className="font-medium text-gray-900">{product.product_name}</div>
                                            {product.ean_upc && (
                                              <div className="text-gray-500">{product.ean_upc}</div>
                                            )}
                                          </div>
                                        </td>
                                        <td className="px-3 py-3 text-sm text-gray-900 min-w-[96px] max-w-[96px]">
                                          {editingShipperId === shipper.id ? (
                                            <div className="flex items-center">
                                              {/* Fixed width container for old value - ensures field alignment */}
                                              <div className="w-16 flex items-center justify-end text-xs text-gray-500">
                                                {(hasQuantityChanges(shipper.id, product.id) || product.isNewlyAdded || product.removed) ? (
                                                  <div className="flex items-center">
                                                    <span>{getOriginalQuantity(shipper.id, product.id)}</span>
                                                    <span className="text-gray-400">→</span>
                                                  </div>
                                                ) : (
                                                  <span className="text-transparent">0</span>
                                                )}
                                              </div>
                                              <div className="flex items-center">
                                                <input
                                                  type="text"
                                                  inputMode="numeric"
                                                  pattern="[0-9]*"
                                                  value={product.quantity === '' || product.quantity === null || product.quantity === undefined ? '' : String(product.quantity)}
                                                  onChange={(e) => {
                                                    const inputValue = e.target.value;
                                                    
                                                    // Allow empty string for better UX
                                                    if (inputValue === '') {
                                                      updateContainedProductQuantity(shipper.id, product.id, '');
                                                      return;
                                                    }
                                                    
                                                    // Only allow digits
                                                    if (!/^\d+$/.test(inputValue)) {
                                                      return;
                                                    }
                                                    
                                                    // Keep as string while typing, but clean leading zeros
                                                    let cleanedValue = inputValue;
                                                    if (inputValue.startsWith('0') && inputValue.length > 1) {
                                                      cleanedValue = inputValue.replace(/^0+/, '') || '0';
                                                    }
                                                    
                                                    const newQuantity = parseInt(cleanedValue) || 0;
                                                    updateContainedProductQuantity(shipper.id, product.id, cleanedValue);
                                                    if (newQuantity === 0) {
                                                      removeContainedProduct(shipper.id, product.id);
                                                    }
                                                  }}
                                                  onBlur={(e) => {
                                                    // Clean the value when clicking away - ensure it's a clean integer
                                                    const cleanValue = parseInt(e.target.value) || 0;
                                                    updateContainedProductQuantity(shipper.id, product.id, cleanValue);
                                                    if (cleanValue === 0) {
                                                      removeContainedProduct(shipper.id, product.id);
                                                    }
                                                  }}
                                                  className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                                />
                                                {getShipperQuantityWarning(shipper.id, product.id) && (
                                                  <div className="ml-2 relative group">
                                                    <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600" />
                                                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none whitespace-nowrap z-50">
                                                      {getShipperQuantityWarning(shipper.id, product.id)}
                                                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                                    </div>
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          ) : (
                                            <div className="flex items-center">
                                              {/* Fixed width container for old value - ensures field alignment */}
                                              <div className="w-16 flex items-center justify-end text-xs text-gray-500">
                                                {(hasQuantityChanges(shipper.id, product.id) || product.isNewlyAdded || product.removed) ? (
                                                  <div className="flex items-center">
                                                    <span>{getOriginalQuantity(shipper.id, product.id)}</span>
                                                    <span className="text-gray-400">→</span>
                                                  </div>
                                                ) : (
                                                  <span className="text-transparent">0</span>
                                                )}
                                              </div>
                                              <span className="text-sm">{product.quantity} units</span>
                                            </div>
                                          )}
                                        </td>
                                        <td className="px-3 py-3 text-right text-sm font-medium">
                                          {editingShipperId === shipper.id && !product.removed && (
                                            <button
                                              onClick={() => removeContainedProduct(shipper.id, product.id)}
                                              className="text-red-600 hover:text-red-800 text-sm"
                                              title="Remove product"
                                            >
                                              Remove
                                            </button>
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <div className="text-gray-500">
                                No products contained in this shipper.
                              </div>
                            )}
                            {/* Add Product Search and Save/Cancel buttons for contained products */}
                            {editingShipperId === shipper.id && (
                              <div className="mt-4 flex justify-between items-center gap-4 relative overflow-visible">
                                {/* Add Product Search */}
                                <div className="flex-1 max-w-md relative overflow-visible" ref={searchRef}>
                                  <div className="relative">
                                    <input
                                      type="text"
                                      placeholder="Search products to add by name, identifier, or product line..."
                                      className="w-full px-3 py-2 pr-16 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                      value={productSearchTerm}
                                      onChange={(e) => {
                                        setProductSearchTerm(e.target.value);
                                        if (e.target.value.trim()) {
                                          setShowProductDropdown(true);
                                          setIsSearching(true);
                                        } else {
                                          setIsSearching(false);
                                        }
                                      }}
                                      onClick={() => {
                                        if (!showProductDropdown) {
                                          setShowProductDropdown(true);
                                        }
                                      }}
                                      autoFocus
                                    />
                                    
                                    {/* Clear button */}
                                    {productSearchTerm && (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setProductSearchTerm('');
                                          setIsSearching(false);
                                        }}
                                        className="absolute right-8 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 hover:text-gray-600 transition-colors"
                                        title="Clear search"
                                      >
                                        <XMarkIcon className="h-4 w-4" />
                                      </button>
                                    )}
                                    
                                    {/* Dropdown toggle button */}
                                    <button
                                      type="button"
                                      onClick={() => setShowProductDropdown(!showProductDropdown)}
                                      className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 hover:text-gray-600 transition-transform"
                                    >
                                      <ChevronDownIcon 
                                        className={`h-4 w-4 transition-transform ${
                                          showProductDropdown ? 'rotate-180' : ''
                                        }`}
                                      />
                                    </button>
                                  </div>
                                  
                                                                    {/* Simple Dropdown */}
                                  {showProductDropdown && (
                                    <div 
                                      data-dropdown
                                      className="absolute z-50 mt-1 w-full max-h-60 overflow-y-auto border-2 border-gray-300 rounded-lg bg-white shadow-xl"
                                    >
                                      {getFilteredAvailableProducts(shipper.id).length > 0 ? (
                                        getFilteredAvailableProducts(shipper.id).map((product) => (
                                          <button
                                            key={product.id}
                                            onClick={() => {
                                              addNewProduct(shipper.id, product.id, product.product_name, product.ean_upc || '');
                                              setProductSearchTerm('');
                                              setShowProductDropdown(false);
                                              setIsSearching(false);
                                            }}
                                            className="w-full text-left p-2 hover:bg-gray-100 border-b border-gray-200 last:border-b-0"
                                          >
                                            <div className="flex justify-between items-start">
                                              <div className="flex-1">
                                                <div className="text-sm font-medium text-gray-900">{product.product_name}</div>
                                                                                            {product.ean_upc && (
                                              <div className="text-xs text-gray-500">{product.ean_upc}</div>
                                            )}
                                              </div>
                                              {product.product_line && (
                                                <div className="text-xs text-gray-400 ml-2 text-right">
                                                  {product.product_line}
                                                </div>
                                              )}
                                            </div>
                                          </button>
                                        ))
                                      ) : (
                                        <div className="p-3 text-gray-500 text-sm">
                                          {productSearchTerm ? 'No products found matching your search.' : 'No available products to add.'}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                                
                                {/* Products Validation Error */}
                                {editingShipperId === shipper.id && getShipperProductsError(shipper.id) && (
                                  <div className="mt-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-md border border-red-200">
                                    {getShipperProductsError(shipper.id)}
                                  </div>
                                )}
                                
                                {/* Save/Cancel buttons */}
                                <div className="flex gap-2">
                                  <SaveCancelButtons 
                                    onSave={() => saveInlineEdit(shipper.id)}
                                    onCancel={() => stopInlineEditing(shipper.id)}
                                    isSaving={isSaving}
                                    hasChanges={hasAnyChanges(shipper.id)}
                                    hasGtinErrors={gtinValidationErrors[shipper.id]?.hasError || false}
                                    savingShipperId={savingShipperId}
                                    shipperId={shipper.id}
                                    size="md"
                                    productsValidationError={getShipperProductsError(shipper.id)}
                                  />
                                </div>
                              </div>
                            )}
                            

                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* New Shipper Modal */}
      <NewShipperForm
        isOpen={showNewShipperModal}
        onClose={closeNewShipperModal}
        onSave={saveNewShipper}
        isSaving={isSaving}
        data={newShipperData}
        onNameChange={(value) => setNewShipperData(prev => ({ ...prev, name: value }))}
        onGtinChange={(value) => setNewShipperData(prev => ({ ...prev, gtin: value }))}
        onGTINValidation={handleNewShipperGTINValidation}
        products={newShipperProducts}
        onAddProduct={addProductToNewShipper}
        onUpdateProductQuantity={updateNewShipperProductQuantity}
        onRemoveProduct={removeProductFromNewShipper}
        hasChanges={hasNewShipperChanges()}
        isValid={isNewShipperFormValid()}
        searchRef={newShipperSearchRef}
        getProductsError={getNewShipperProductsError}
        productSearchTerm={newShipperProductSearchTerm}
        showProductDropdown={newShipperShowProductDropdown}
        isSearching={newShipperIsSearching}
        onProductSearchChange={handleNewShipperProductSearch}
        onToggleProductDropdown={toggleNewShipperProductDropdown}
        onClearProductSearch={clearNewShipperProductSearch}
        onAddProductFromSearch={addProductToNewShipperFromSearch}
        filteredProducts={getFilteredProductsForNewShipper()}
        getQuantityWarning={getNewShipperQuantityWarning}
      />
    </div>
  );
};

export default Shippers;
